import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

export default function usePromotionList() {
  const toast = useToast()
  const route = router.history.current

  const refPromotionsTable = ref(null)

  const tableColumns = [
    {
      key: 'name',
      label: 'Name and Code',
      sortable: true,
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
    },
    {
      key: 'value',
      label: 'Value',
      sortable: false,
    },
    {
      key: 'usedQuota',
      label: 'Current Usage',
      sortable: false,
    },
    {
      key: 'totalQuota',
      label: 'Total Quota',
      sortable: false,
    },
    {
      key: 'quotaPerAgent',
      label: 'Quota/Agent',
      sortable: false,
    },
    {
      key: 'validityStartDate',
      label: 'Start Date',
      sortable: true,
    },
    {
      key: 'validityEndDate',
      label: 'End Date',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
    },
    { key: 'actions' },
  ]

  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalPromotions = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'createdAt')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc !== 'false') : true)

  const typeFilter = ref([])
  if (Array.isArray(route.query.type)) {
    typeFilter.value = route.query.type
  } else if (route.query.type) {
    typeFilter.value = [route.query.type]
  }

  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }

  const toDeletedID = ref(null)
  const currentDataLength = ref(0)
  const itemListData = ref([])
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refPromotionsTable.value ? refPromotionsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalPromotions.value,
    }
  })

  const refetchData = () => {
    refPromotionsTable.value.refresh()
  }

  const fetchPromotions = () => {
    store
      .dispatch('app-promotions/fetchPromotions', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        type: typeFilter.value,
        status: statusFilter.value,
      },
      { root: true })
      .then(response => {
        const { promotions, total } = response.data
        itemListData.value = promotions
        currentDataLength.value = promotions.length
        totalPromotions.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              type: typeFilter.value,
              status: statusFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  let timer = null
  watch([typeFilter, statusFilter], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      fetchPromotions()
    }, 1000)
  }, { deep: true })

  watch([searchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 2)) {
        currentPage.value = 1
        fetchPromotions()
      }
    }, 1000)
  }, { deep: true })

  watch([sortBy, perPage, isSortDirDesc, currentPage], () => {
    fetchPromotions()
  }, { deep: true })

  fetchPromotions()

  const deletePromotion = () => {
    store
      .dispatch('app-promotions/deletePromotion', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        fetchPromotions()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const resolveStatusVariant = status => {
    if (status === 'Active') return 'success'
    if (status === 'Inactive') return 'secondary'
    return 'primary'
  }

  return {
    fetchPromotions,
    tableColumns,
    perPage,
    currentPage,
    totalPromotions,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refPromotionsTable,
    refetchData,

    toDeletedID,
    deletePromotion,
    currentDataLength,
    itemListData,

    resolveStatusVariant,

    typeFilter,
    statusFilter,
  }
}
